import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Formik, Form } from 'formik';
import { ValidateForm } from 'Utils/Forms';
import { UpdateAuthDataValues, ChangePasswordData } from 'Types';
import UpdateProfileForm, { updateProfileFormSchema } from 'Components/Forms/UpdateProfileForm';
import ChangePasswordForm, { changePasswordFormSchema } from 'Components/Forms/ChangePasswordForm';
import VerificationCode from 'Containers/SignIn/VerificationCode';
import { useEffect } from 'react';
import { isEmail, isPhoneNumber } from 'Components/Forms/SignUpForm';
import * as amplify from 'Utils/Amplify';
import { CustomCircularProgress } from 'Components/StyledComponents';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import Cancel from './Cancel';
const Account = () => {
  const {
    user,
    pendingEmailOrPhone,
    errors,
    successMessage,
    processPending,
    updateAuthDataCognito,
    changePassword,
    verifyAttributes,
    verifyAttributesSubmit,
    resetErrors,
    isOidcUser,
  } = useAuthWrapperContext();

  const [showUnverifiedMessage, setShowUnverifiedMessage] = React.useState<boolean>(false);

  useEffect(() => {
    amplify
      .verifiedContact()
      .then(({ unverified }) => {
        const unv = unverified as Record<string, unknown>;
        if (!isOidcUser && unv?.email) {
          setShowUnverifiedMessage(true);
        }
        if (!isOidcUser && unv?.phone_number) {
          setShowUnverifiedMessage(true);
        }
      })
      .catch(console.error);
  }, [user, isOidcUser]);

  return (
    <Box sx={{ height: 'calc(100vh - 200px)' }}>
      <Box
        sx={{
          height: 'calc(100vh - 156px)',
          width: '100%',
          overflow: 'scroll',
          paddingTop: '20px',
        }}
      >
        {/* </Box> */}
        {!isOidcUser && (
          <Box
            sx={{
              marginLeft: '50px',
              width: '600px',
              paddingTop: '20px',
              paddingBottom: '20px',
              backgroundColor: 'black',
              borderRadius: '5px',
            }}
          >
            <Box
              sx={{
                color: 'white',
                textAlign: 'left',
                marginLeft: '24px',
                marginRight: '24px',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'Lato',
              }}
            >
              {processPending && <CustomCircularProgress />}
              {!pendingEmailOrPhone && !processPending && !isOidcUser && (
                <>
                  <Box
                    sx={{
                      marginBottom: '20px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: '26px',
                      letterSpacing: '0.01em',
                      color: '#FFFFFF',
                    }}
                  >
                    Update Profile
                  </Box>

                  <Formik
                    initialValues={{
                      name: '',
                      emailOrPhone: '',
                      countryAcronym: 'US',
                      unknown: '',
                    }}
                    validate={(values: UpdateAuthDataValues) => {
                      return ValidateForm(updateProfileFormSchema, values);
                    }}
                    onSubmit={async (values: UpdateAuthDataValues) => {
                      await updateAuthDataCognito({ payload: values });
                    }}
                    validateOnChange={false}
                  >
                    {(formikProps) => (
                      <Form
                        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                          e.preventDefault();
                          resetErrors();
                          formikProps.handleSubmit(e);
                        }}
                      >
                        <Box sx={{ width: '350px' }}>
                          <UpdateProfileForm
                            successMessage={successMessage}
                            placeholders={{
                              name: user?.attributes?.name || '',
                              emailOrPhone:
                                user?.attributes?.email || user?.attributes?.phone_number || '',
                              countryAcronym: '',
                              unknown: '',
                            }}
                            disabled={{ emailOrPhone: isOidcUser, name: isOidcUser }}
                            {...formikProps}
                            errors={{ ...errors, ...formikProps.errors }}
                          />
                        </Box>
                      </Form>
                    )}
                  </Formik>
                </>
              )}
              {showUnverifiedMessage && !pendingEmailOrPhone && !processPending && (
                <Button
                  variant="text"
                  onClick={async () => {
                    await verifyAttributes();
                  }}
                  sx={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                    paddingBottom: '20px',
                  }}
                >
                  {isPhoneNumber(pendingEmailOrPhone) &&
                    'Your Phone number is unverified. Please click here to recieve a verification code'}
                  {isEmail(pendingEmailOrPhone) &&
                    'Your email is unverified. Please click here to receive a confirmation link'}
                </Button>
              )}
              {pendingEmailOrPhone && !processPending && (
                <VerificationCode
                  onSubmit={async (confirmationCode) => {
                    await verifyAttributesSubmit(confirmationCode);
                  }}
                />
              )}
              {/* Cancel/Deletion actions */}
            </Box>
          </Box>
        )}
        {!isOidcUser && (
          <Box
            sx={{
              marginLeft: '50px',
              width: '600px',
              marginTop: '20px',
              backgroundColor: 'black',
              borderRadius: '5px',
            }}
          >
            <Box
              sx={{
                color: 'white',
                textAlign: 'left',
                marginLeft: '24px',
                marginRight: '24px',
                marginTop: '20px',
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '40px',
              }}
            >
              <Box
                sx={{
                  paddingTop: '20px',
                  marginBottom: '20px',
                  fontStyle: 'normal',
                  fontWeight: '400',
                  fontSize: '26px',
                  letterSpacing: '0.01em',
                  color: '#FFFFFF',
                  fontFamily: 'Lato',
                }}
              >
                Change Password
              </Box>

              <Formik
                initialValues={{
                  currentPassword: '',
                  newPassword: '',
                  repeatNewPassword: '',
                  unknown: '',
                }}
                validate={(values: ChangePasswordData) => {
                  if (values.newPassword !== values.repeatNewPassword) {
                    return {
                      newPassword: 'Passwords must match',
                      repeatNewPassword: 'Passwords must match',
                    };
                  }
                  return ValidateForm(changePasswordFormSchema, values);
                }}
                onSubmit={async (values) => {
                  await changePassword({ payload: values });
                }}
                validateOnChange={false}
              >
                {(formikProps) => (
                  <Form
                    onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                      e.preventDefault();
                      resetErrors();
                      formikProps.handleSubmit(e);
                    }}
                  >
                    <Box sx={{ width: '350px' }}>
                      <ChangePasswordForm
                        successMessage={successMessage}
                        {...formikProps}
                        errors={{ ...errors, ...formikProps.errors }}
                      />
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            marginLeft: '50px',
            width: '600px',
            marginTop: '20px',
            backgroundColor: 'black',
            borderRadius: '5px',
          }}
        >
          <Box
            sx={{
              color: 'white',
              textAlign: 'left',
              marginLeft: '24px',
              marginRight: '24px',
              marginTop: '20px',
              display: 'flex',
              flexDirection: 'column',
              marginBottom: '40px',
            }}
          >
            <Box
              sx={{
                paddingTop: '20px',
                marginBottom: '20px',
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '26px',
                letterSpacing: '0.01em',
                color: '#FFFFFF',
                fontFamily: 'Lato',
              }}
            >
              Manage Subscription
            </Box>
            <Cancel />
          </Box>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{ color: '#fff', position: 'fixed', bottom: 16, right: 16 }}
      >
        version: {process.env.REACT_APP_LAST_COMMIT ?? 'unknown'}
      </Typography>
    </Box>
  );
};

export default Account;
